import { useCallback, useEffect, useState } from 'react';
import { useLocale } from '@/i18n/locale-context';
import { printPage } from '@/lib/dom';
import { useToast } from '@/components/ui';
import { Icon, type IconName } from '@/components/shell/Icon';

/**
 * Share targets for an article (PRD §15), ready for Phase 9.
 *
 * Plain links to each network's share endpoint rather than their SDKs: a third-party
 * script on every article page costs a request, a tracker and a CSP exception, and buys
 * a button that already works as a URL. Copy-link uses the async clipboard API with a
 * `document.execCommand` fallback for browsers that refuse it on non-HTTPS origins.
 */
export interface ShareBarProps {
  /** Absolute URL. Relative paths break every one of these endpoints. */
  url: string;
  title: string;
  /**
   * Called with the target a reader chose (Phase 16). Optional, because this component
   * is also used on the video page and in places where there is no article to attribute
   * a share to — a share of nothing in particular is not worth an event.
   */
  onShare?: (network: string) => void;
}

const TARGETS: { name: IconName; label: string; href: (url: string, title: string) => string }[] = [
  {
    name: 'whatsapp',
    label: 'WhatsApp',
    href: (url, title) => `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
  },
  {
    name: 'facebook',
    label: 'Facebook',
    href: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    name: 'x',
    label: 'X',
    href: (url, title) =>
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
];

export function ShareBar({ url, title, onShare }: ShareBarProps) {
  const { t } = useLocale();
  const toast = useToast();
  const [canShareNatively, setCanShareNatively] = useState(false);

  // Feature-detected after mount, never during render: `navigator` does not exist on the
  // server, and a button that appears only on the client must not be in the server's HTML.
  useEffect(() => {
    setCanShareNatively(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      onShare?.('copy');
      toast.show(t('share.copied'), 'success');
    } catch {
      toast.show(t('error.genericTitle'), 'error');
    }
  }, [toast, t, url, onShare]);

  const shareNatively = useCallback(async () => {
    try {
      await navigator.share({ title, url });
      // Only after it resolves: the sheet rejects when it is dismissed, and counting a
      // share the reader backed out of would inflate the one figure editors trust most.
      onShare?.('native');
    } catch {
      // An abandoned share sheet rejects. That is not an error worth telling anyone about.
    }
  }, [title, url, onShare]);

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      role="group"
      aria-label={t('action.share')}
      data-print-hide
    >
      <span className="text-xs font-extrabold uppercase tracking-wide text-muted">
        {t('action.share')}
      </span>
      {TARGETS.map((target) => (
        <a
          key={target.name}
          href={target.href(url, title)}
          target="_blank"
          rel="noreferrer noopener"
          aria-label={target.label}
          onClick={() => onShare?.(target.name)}
          className="grid h-8 w-8 place-items-center rounded-full border border-line text-nav-idle transition hover:border-red hover:text-red"
        >
          <Icon name={target.name} size={14} />
        </a>
      ))}
      <button
        type="button"
        onClick={() => void copy()}
        aria-label={t('share.copyLink')}
        className="grid h-8 w-8 place-items-center rounded-full border border-line text-nav-idle transition hover:border-red hover:text-red"
      >
        <Icon name="link" size={14} />
      </button>

      {/* Phones have a share sheet that reaches apps this row never will. */}
      {canShareNatively && (
        <button
          type="button"
          onClick={() => void shareNatively()}
          aria-label={t('share.more')}
          className="grid h-8 w-8 place-items-center rounded-full border border-line text-nav-idle transition hover:border-red hover:text-red"
        >
          <Icon name="share" size={14} />
        </button>
      )}

      <button
        type="button"
        onClick={() => {
          onShare?.('print');
          printPage();
        }}
        aria-label={t('action.print')}
        className="grid h-8 w-8 place-items-center rounded-full border border-line text-nav-idle transition hover:border-red hover:text-red"
      >
        <Icon name="print" size={14} />
      </button>
    </div>
  );
}
